/**
 * 業務フローの図の右パネル — 業務を選んだときだけ開き、業務名・業務詳細へのボタン・業務ルールの並びを出す。
 *
 * **出すのは 3 つ**（Dhik 2026-09-30「業務フローの業務をクリックしたときに以下の情報を右側に出して
 * ほしい — タイトル / 「業務詳細」ボタン / 業務ルールのリスト（リンク付き）」）。
 *
 * | 出すもの | 中身 |
 * |---|---|
 * | タイトル | 業務名。レーン（アクター）は業務名を指すと出す |
 * | 「業務詳細」ボタン | 業務詳細へのリンク。案件を引き継ぐ |
 * | 業務ルール | 業務詳細と同じ名前の順で並べ、押すとその業務ルールの画面（`/rules/$ruleId`）へ移る |
 *
 * 結末と宛先の表は図の線と枝のラベルが同じことを言うので出さない。案件から見た図では、案件で見る人が
 * 一番知りたい「この PJ で変えた理由」をボタンの下に添える。
 *
 * **業務ルールは業務詳細の読取（`useBusinessDetail`）で選んだ業務 1 件ぶんだけ引く。** 業務フローの
 * 読取は節点に業務の名前しか載せない。API を足すより、業務詳細と同じ口を叩くほうが、業務詳細の画面と
 * 右パネルで並ぶ業務ルールが食い違わない。案件から見た図では、押した側（変更前 ／ 変更後）の姿の
 * 業務ルールを出す。
 *
 * **開始・終了・菱形では開かない。** 中身（入口の文・並列の説明）は図の節点を指すと出る。
 * 何も選んでいないときも開かない（図の読み方は図の枠の ⓘ にある）。
 *
 * 枠（見出し・✕・なぜ変えたか）は共通の部品（`flow-workspace/flow-detail-shell.tsx`）が持つ。
 *
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローで選んだ業務の名前・業務詳細へのボタン・業務ルールの並び（業務ルールの画面へのリンク）を右パネルに描き、案件から見た図では変えた理由を添える。
 * @graph-connects FlowDetailShell [calls] 詳細ペインの外枠
 * @graph-connects useBusinessDetail [calls] 選んだ業務の業務ルールの取得
 */
import type {
  BusinessDetailPair,
  BusinessDetailView,
  FlowNodeView,
} from '@cortex/domain-spec-console';
import { Skeleton } from '@cortex/web-design-system/components/ui/Skeleton';
import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import type { DiffSide } from '../../components/spec/DiffSplitPanes.js';
import { displayLabel } from '../../components/spec/UntranslatedBadge.js';
import { useBusinessDetail } from '../../hooks/queries/spec-graph-business-detail-queries.js';
import { FlowDetailShell } from '../flow-workspace/flow-detail-shell.js';
import { pjLinkSearch } from '../pj-link.js';
import { BUSINESS_DETAIL_ROUTE } from '../route-paths.js';
import { DASH } from '../screen-detail-cells.js';
import { FIELD_HEAD_CLASS, ITEM_CLASS, useEscapeToClose } from '../screen-detail-panel.js';
import { RuleLink } from '../view-links/business-logic.js';
import { pjLinkValue } from '../view-links/common.js';
import { flowNodeLabelOf, type FlowNodeLabels } from '../business-flow-diagram/nodes.js';

/**
 * 「業務詳細」ボタンの見た目。リンクだが、押す先が 1 つのボタンとして目に入るよう枠で囲む。
 * @graph-connects none
 */
const DETAIL_BUTTON_CLASS =
  'mt-2 inline-flex w-fit items-center rounded-sm border border-[var(--color-accent-primary)] px-3 py-1 text-sm text-[var(--color-accent-primary)] hover:bg-[var(--color-bg-hover)]';

/** `StoryFlowDetail` の props。 @graph-connects none */
export interface StoryFlowDetailProps {
  /** 図の中で開いている業務の節点 */
  selected: FlowNodeView;
  /** 重ねる案件番号。リンクに引き継ぐ。master なら undefined */
  pjNumber?: number;
  /** この節点をなぜそう変えたか */
  whyChanged?: readonly string[];
  /** 節点を押した面。案件から見た図で、どちらの姿の業務ルールを出すか。省けば変更後 */
  side?: DiffSide;
  /** ✕ ・ Esc で右パネルを閉じる */
  onClose: () => void;
}

/**
 * 節点の語を訳から組む。図と詳細が同じ語で節点を呼ぶ。
 *
 * @param t 訳
 * @returns 節点の語
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの節点 5 種の表示の語を訳から組む。
 * @graph-connects none
 */
export function flowNodeLabelsOf(t: (key: string) => string): FlowNodeLabels {
  return {
    start: t('storyFlow.nodeStart'),
    end: t('storyFlow.nodeEnd'),
    parallelSplit: t('flow.nodeParallelSplit'),
    parallelJoin: t('flow.nodeParallelJoin'),
    businessMissing: t('storyFlow.businessMissing'),
  };
}

/**
 * 右パネルを開く節点か。業務の節点だけが開く。
 *
 * @param node 押した節点。何も押していなければ null
 * @returns 右パネルを開くなら true
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローの図で右パネルを開く節点（業務）かを判定する。
 * @graph-connects none
 */
export function opensFlowDetail(node: FlowNodeView | null): node is FlowNodeView {
  return node !== null && node.kind === 'business';
}

/**
 * 業務詳細の読取の結果から、押した面の姿を選ぶ。
 *
 * 押した面に業務が無ければ（案件が作った業務の変更前・消した業務の変更後）、もう片方の姿を使う。
 * master の読取は変更後の側に姿を載せる。
 *
 * @param pair 業務詳細の変更前と変更後
 * @param side 押した面
 * @returns 押した面の業務の姿。どちらにも無ければ null
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務詳細の変更前と変更後から、業務フローの図で押した面の業務の姿を選ぶ。
 * @graph-connects none
 */
export function businessDetailOfSide(
  pair: BusinessDetailPair,
  side: DiffSide,
): BusinessDetailView | null {
  return side === 'before' ? (pair.before ?? pair.after) : (pair.after ?? pair.before);
}

/**
 * 選んだ業務の右パネルを描く。
 *
 * @param props 選んだ業務・案件番号・なぜ変えたか・押した面・閉じる口
 * @returns 業務名・業務詳細へのボタン・なぜ変えたか・業務ルールの並び
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローで選んだ業務の名前を見出しに、業務詳細へのボタンと業務ルールの並びを右パネルに描き、変えた理由を添える。Esc で閉じる。
 * @graph-connects FlowDetailShell [calls] 詳細ペインの外枠
 * @graph-connects flowNodeLabelOf [calls] 業務名（未記入なら差し替えの語）
 * @graph-connects flowNodeLabelsOf [calls] 節点の語
 * @graph-connects useEscapeToClose [calls] Esc で閉じる
 * @graph-connects StoryFlowBusinessRules [calls] 業務ルールの並び
 */
export function StoryFlowDetail({
  selected,
  pjNumber,
  whyChanged = [],
  side = 'after',
  onClose,
}: StoryFlowDetailProps) {
  const { t } = useTranslation();
  /* 右パネルはこの部品が描かれている間だけ開いている。 */
  useEscapeToClose(true, onClose);
  const name = flowNodeLabelOf(selected, flowNodeLabelsOf(t));
  const lane = t('storyFlow.detailLane', {
    lane: selected.actor === null ? t('spec.unset') : displayLabel(selected.actor.label, t),
  });
  const { business } = selected;

  return (
    <FlowDetailShell
      label={t('storyFlow.detailPanelLabel')}
      onClose={onClose}
      heading={
        <span
          title={lane}
          data-id={selected.nodeId}
          data-detail-kind={selected.kind}
          data-flow-detail="title"
        >
          {name}
        </span>
      }
      lead={
        business !== null && (
          <Link
            to={BUSINESS_DETAIL_ROUTE}
            params={{ businessId: String(business.id) }}
            {...pjLinkSearch(pjLinkValue(pjNumber))}
            className={DETAIL_BUTTON_CLASS}
            data-flow-detail="business"
          >
            {t('storyFlow.detailOpenBusiness')}
          </Link>
        )
      }
      whyChanged={whyChanged}
    >
      {business !== null && (
        <StoryFlowBusinessRules businessId={business.id} pjNumber={pjNumber} side={side} />
      )}
    </FlowDetailShell>
  );
}

/** `StoryFlowBusinessRules` の props。 @graph-connects none */
interface StoryFlowBusinessRulesProps {
  /** 選んだ業務の id */
  businessId: number;
  /** 重ねる案件番号。master なら undefined */
  pjNumber: number | undefined;
  /** 節点を押した面 */
  side: DiffSide;
}

/**
 * 選んだ業務の業務ルールの並び。名前の順に並べ、押すとその業務ルールの画面（`/rules/$ruleId`）へ移る。
 *
 * 業務ルールが 1 件も無ければ「—」を出して節ごと残す。節を消すと「無い」のか「読めていない」のかが
 * 右パネルから読めない。
 *
 * @param props 業務の id・案件番号・押した面
 * @returns 業務ルールの節
 * @graph-node {Function}
 * @graph-stack spec-console
 * @graph-domain Engineering
 * @graph-business 業務フローで選んだ業務の業務ルールを業務詳細の読取から引き、名前の順に業務ルールの画面へのリンクとして並べる。
 * @graph-connects useBusinessDetail [calls] 選んだ業務の業務詳細の取得
 * @graph-connects businessDetailOfSide [calls] 押した面の業務の姿
 * @graph-connects RuleLink [calls] 業務ルールの画面へのリンク
 */
function StoryFlowBusinessRules({ businessId, pjNumber, side }: StoryFlowBusinessRulesProps) {
  const { t, i18n } = useTranslation();
  const query = useBusinessDetail(businessId, pjNumber);
  const title = t('businessLogic.rulesField');

  const body = (() => {
    if (query.error !== null && query.error !== undefined) {
      return <p className="text-sm text-[var(--color-text-muted)]">{t('common.error')}</p>;
    }
    if (query.data === undefined) {
      return (
        <div aria-busy="true" aria-label={t('common.loading')}>
          <Skeleton variant="rectangular" height="4rem" />
        </div>
      );
    }
    const rules = businessDetailOfSide(query.data, side)?.rules ?? [];
    if (rules.length === 0) {
      return <p className="text-sm text-[var(--color-text-muted)]">{DASH}</p>;
    }
    const nameOf = (rule: (typeof rules)[number]) => displayLabel(rule.label, t, rule.untranslated);
    const sorted = [...rules].sort((left, right) =>
      nameOf(left).localeCompare(nameOf(right), i18n.resolvedLanguage),
    );
    return (
      <ul>
        {sorted.map((rule) => (
          <li key={rule.id} className={ITEM_CLASS} data-flow-rule={rule.id}>
            <RuleLink ruleId={rule.id} pjNumber={pjNumber}>
              {nameOf(rule)}
            </RuleLink>
          </li>
        ))}
      </ul>
    );
  })();

  return (
    <section aria-label={title} data-flow-detail="rules">
      <h3 className={FIELD_HEAD_CLASS}>{title}</h3>
      {body}
    </section>
  );
}
